import React, { type RefObject } from 'react';

import { AuditTimelineItem } from '@apps/web/components/shared/messages/audit_timeline_item';
import { CallTimelineItem } from '@apps/web/components/shared/messages/call_timeline_item';
import { LedgerTimelineItem } from '@apps/web/components/shared/messages/ledger_timeline_item';
import { MessageItem } from '@apps/web/components/shared/messages/message_item';
import {
    type ConversationTimelineEntry,
    formatTimelineTimestamp,
    getConversationTimelineTimestamp,
    latestOutboundMessageId,
    showTimelineTimestamp,
} from '@apps/web/components/shared/messages/messages_timeline_utils';
import { type MessageRatingLabel } from '@libraries/typescript/api_client';

type SmsRunRating = {
    readonly run_id: string;
    readonly rating_label: MessageRatingLabel | null;
};

type ConversationTimelineProps = {
    readonly entries: ReadonlyArray<ConversationTimelineEntry>;
    readonly timezone: string;
    readonly highlightedMessageRef: RefObject<HTMLDivElement | null>;
    readonly highlightMessageId?: string | null;
    readonly highlightLabel: string;
    readonly searchActive: boolean;
    readonly searchQuery?: string | null;
    readonly activeSearchMatchId?: string | null;
    readonly showRating: boolean;
    readonly recordingUrls?: Readonly<Record<string, string>>;
    readonly onOpenCall: (callId: string) => void;
    readonly onOpenCaregiverMemory: (caregiverId: string) => void;
    readonly runIdByMessageId: Readonly<Record<string, string>>;
    readonly organizationId?: string | null;
    readonly smsTrainingReview: boolean;
    readonly smsRunRatingsByMessageId: Readonly<
        Record<string, ReadonlyArray<SmsRunRating>>
    >;
    readonly onSmsRunRated?: () => void;
    readonly contactName?: string | null;
    readonly contactColorKey?: string;
    readonly showMessageNotes?: boolean;
    readonly openNotesForMessageId?: string | null;
    readonly onConversationMutated?: () => Promise<unknown> | void;
};

function TimelineTimestampSeparator({
    timestamp,
    timezone,
}: {
    readonly timestamp: string;
    readonly timezone: string;
}) {
    const { dayLabel, timeLabel } = formatTimelineTimestamp(
        timestamp,
        timezone,
    );
    return (
        <div className="text-muted-foreground my-4 text-center text-xs">
            <span className="font-medium">{dayLabel}</span>, {timeLabel}
        </div>
    );
}

export function ConversationTimeline({
    entries,
    timezone,
    highlightedMessageRef,
    highlightMessageId,
    highlightLabel,
    searchActive,
    searchQuery,
    activeSearchMatchId,
    showRating,
    recordingUrls,
    onOpenCall,
    onOpenCaregiverMemory,
    runIdByMessageId,
    organizationId,
    smsTrainingReview,
    smsRunRatingsByMessageId,
    onSmsRunRated,
    contactName = null,
    contactColorKey,
    showMessageNotes = false,
    openNotesForMessageId = null,
    onConversationMutated,
}: ConversationTimelineProps) {
    const deliveryReceiptMessageId = latestOutboundMessageId(entries);
    return entries.map((entry, index) => {
        const previousEntry = index > 0 ? (entries[index - 1] ?? null) : null;
        const entryTimestamp = getConversationTimelineTimestamp(entry);
        const showTimestamp = showTimelineTimestamp(
            previousEntry === null
                ? null
                : getConversationTimelineTimestamp(previousEntry),
            entryTimestamp,
            timezone,
        );

        return (
            <React.Fragment key={`${entry.type}-${entry.id}`}>
                {showTimestamp && (
                    <TimelineTimestampSeparator
                        timestamp={entryTimestamp}
                        timezone={timezone}
                    />
                )}
                <div
                    ref={
                        entry.type === 'message' &&
                        highlightMessageId === entry.message.id
                            ? highlightedMessageRef
                            : null
                    }
                    data-search-active={
                        entry.type === 'message' &&
                        activeSearchMatchId === entry.message.id
                            ? 'true'
                            : undefined
                    }
                >
                    {entry.type === 'message' ? (
                        <MessageItem
                            message={entry.message}
                            timezone={timezone}
                            isHighlighted={
                                highlightMessageId === entry.message.id
                            }
                            highlightLabel={
                                highlightMessageId === entry.message.id
                                    ? highlightLabel
                                    : undefined
                            }
                            searchQuery={searchActive ? searchQuery : null}
                            isSearchActiveMatch={
                                activeSearchMatchId === entry.message.id
                            }
                            showDeliveryReceipt={
                                deliveryReceiptMessageId === entry.message.id
                            }
                            showRating={showRating}
                            smsEngineRunId={runIdByMessageId[entry.message.id]}
                            organizationId={organizationId}
                            smsTrainingReview={smsTrainingReview}
                            smsRunRatings={
                                smsRunRatingsByMessageId[entry.message.id]
                            }
                            onSmsRunRated={onSmsRunRated}
                            contactName={contactName}
                            contactColorKey={contactColorKey}
                            showMessageNotes={showMessageNotes}
                            openNotesInitially={
                                openNotesForMessageId === entry.message.id
                            }
                            onConversationMutated={onConversationMutated}
                        />
                    ) : entry.type === 'ledger_event' ? (
                        <LedgerTimelineItem
                            label={entry.ledgerEvent.label}
                            kind={entry.ledgerEvent.kind}
                            sentenceParts={entry.ledgerEvent.sentence_parts}
                            onOpenCaregiverMemory={onOpenCaregiverMemory}
                        />
                    ) : entry.type === 'audit' ? (
                        <AuditTimelineItem
                            event={entry.auditEvent}
                            timezone={timezone}
                        />
                    ) : (
                        <CallTimelineItem
                            call={entry.call}
                            timezone={timezone}
                            prefetchedRecordingUrl={
                                recordingUrls?.[entry.call.id]
                            }
                            onOpen={() => onOpenCall(entry.call.id)}
                        />
                    )}
                </div>
            </React.Fragment>
        );
    });
}
